// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import { useAuthData } from '../../providers/auth-provider';
import {
  resolvePostLoginRedirect,
  type PostLoginRedirectToken,
} from '../../utils/post-login-redirect';

export interface OAuthCallbackProps {
  readonly redirectTo?: string;
  readonly postLoginRedirectTokens?: readonly PostLoginRedirectToken[];
}

export const useOAuthCallback = (props: OAuthCallbackProps) => {
  const redirectTo = props.redirectTo ?? '/';
  const postLoginRedirectTokens = props.postLoginRedirectTokens;
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { authorize, isExchangingCode, login } = useAuthData();
  const [error, setError] = useState<string | null>(null);

  const code = searchParams.get('code');
  const state = searchParams.get('state');

  useEffect(() => {
    if (!code) {
      setError(t('auth.callback.missing-code', { ns: 'auth' }));

      return;
    }

    const redirectUri = `${window.location.origin}${window.location.pathname}`;
    const resolvedRedirect = postLoginRedirectTokens?.length
      ? resolvePostLoginRedirect(state)
      : null;
    const destination =
      resolvedRedirect && postLoginRedirectTokens?.includes(resolvedRedirect.token)
        ? resolvedRedirect.path
        : redirectTo;

    authorize({ code, redirectUri, redirectTo: destination })
      .then(result =>
        result.success
          ? navigate(destination, { replace: true })
          : setError(
              t(result.errorKey ?? 'auth.callback.exchange-failed', { ns: 'auth' }),
            ),
      )
      .catch(() => setError(t('auth.callback.exchange-failed', { ns: 'auth' })));
  }, [code, authorize, navigate, postLoginRedirectTokens, redirectTo, state, t]);

  return {
    error,
    isLoading: isExchangingCode,
    login,
    t,
  };
};
